import { useState } from 'react';
import { Clock, Flame, Trophy, Globe, ArrowUpRight, ArrowDownRight, Tag } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import GlassCard from '../components/GlassCard';
import StreakBadge from '../components/StreakBadge';
import { formatTime, formatDuration, timeAgo, countdown, getProgressColor } from '../lib/utils';
import { useTimerStore } from '../store/timerStore';

function StatCard({ icon: Icon, label, value, sub, color, delay = 0 }) {
  return (
    <GlassCard
      className="flex flex-col gap-3 animate-slide-up"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      <div className="flex items-center justify-between">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${color}18`, border: `1px solid ${color}30` }}
        >
          <Icon size={20} style={{ color }} />
        </div>
        <span className="text-white/30 text-xs">{label}</span>
      </div>
      <div>
        <div className="text-2xl font-bold text-white heading">{value}</div>
        {sub && <div className="text-white/40 text-xs mt-1">{sub}</div>}
      </div>
    </GlassCard>
  );
}

function ChallengeCard({ challenge }) {
  const cd = countdown(challenge.deadline);
  const isExpired = cd === 'Expired';
  const isUrgent = !isExpired && (new Date(challenge.deadline) - new Date()) < 24 * 3600000;

  return (
    <div
      className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 lift"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
        style={{ background: 'rgba(99,102,241,0.15)' }}
      >
        ⚔️
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium text-sm">{challenge.title}</div>
        <div className="flex flex-wrap gap-2 mt-1">
          <span className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
            {challenge.topic}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${isExpired ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-white/40'}`}
            style={{ background: isExpired ? 'rgba(239,68,68,0.1)' : isUrgent ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.05)' }}>
            ⏰ {cd}
          </span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-amber-400 font-bold text-sm">+{challenge.xpReward} XP</div>
        <div className="text-white/30 text-xs">{challenge.minHours}h min</div>
      </div>
    </div>
  );
}

function ActivityItem({ session, delay }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl animate-slide-up"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
      }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(99,102,241,0.15)' }}>
        <Clock size={14} className="text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white text-sm font-medium truncate">{session.topic}</div>
        <div className="text-white/40 text-xs">{timeAgo(session.date)}</div>
      </div>
      <div className="text-indigo-400 font-mono text-sm font-semibold flex-shrink-0">
        {formatDuration(session.duration)}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const { challenges, recentActivity, members, weeklyMinimumHours, groupName } = useGroupStore();
  const { isRunning, elapsedSeconds } = useTimerStore();

  const todaySeconds = recentActivity?.[0]?.duration ?? 0;
  const displaySeconds = isRunning ? elapsedSeconds : todaySeconds;

  const streak = user?.streak ?? 0;
  const weeklyHours = user?.weeklyGoalHours ?? 20;

  // Find user rank in members list (sorted by weeklyHours desc)
  const sorted = [...(members || [])].sort((a, b) => b.weeklyHours - a.weeklyHours);
  const userIdx = sorted.findIndex(m => m.id === user?.id);
  const groupRank = userIdx >= 0 ? userIdx + 1 : 1;
  const userMember = sorted.find(m => m.id === user?.id) ?? sorted[0];
  const weeklyLogged = userMember?.weeklyHours ?? 0;
  const progressPct = Math.min(100, Math.round((weeklyLogged / Math.max(weeklyMinimumHours, 1)) * 100));
  const progressColor = getProgressColor(progressPct);

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="heading text-3xl font-bold text-white mb-1">
          Good evening, {(user?.displayName || user?.name || 'there')?.split(' ')[0]} 👋
        </h1>
        <p className="text-white/40 text-sm">
          {groupName} · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* ── 4 Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Clock}
          label="Today's Study Time"
          value={formatTime(displaySeconds)}
          sub={isRunning ? '🟢 Session active' : 'No active session'}
          color="#6366f1"
          delay={0}
        />
        <StatCard
          icon={Flame}
          label="Current Streak"
          value={`${streak} days`}
          sub={streak >= 30 ? '🔴 On fire!' : streak >= 7 ? '🟡 Great momentum' : '🟢 Keep it up!'}
          color={streak >= 30 ? '#ef4444' : streak >= 7 ? '#f59e0b' : '#22c55e'}
          delay={100}
        />
        <StatCard
          icon={Trophy}
          label="Group Rank"
          value={`#${groupRank}`}
          sub={`of ${members?.length || 1} members`}
          color="#f59e0b"
          delay={200}
        />
        <StatCard
          icon={Globe}
          label="Global Rank"
          value="#3"
          sub="of all groups"
          color="#818cf8"
          delay={300}
        />
      </div>

      {/* ── Weekly Progress ── */}
      <GlassCard className="mb-6 animate-slide-up" style={{ animationDelay: '350ms', animationFillMode: 'both' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold">Weekly Progress</h3>
            <p className="text-white/40 text-xs mt-0.5">
              {weeklyLogged.toFixed(1)}h logged · {weeklyMinimumHours}h required
            </p>
          </div>
          <div
            className="px-3 py-1 rounded-full text-sm font-bold"
            style={{
              background: progressPct >= 80 ? 'rgba(34,197,94,0.15)' : progressPct >= 50 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
              color: progressPct >= 80 ? '#22c55e' : progressPct >= 50 ? '#f59e0b' : '#ef4444',
            }}
          >
            {progressPct}%
          </div>
        </div>
        <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div
            className={`h-full rounded-full transition-all duration-1000 ${progressColor}`}
            style={{
              width: `${progressPct}%`,
              boxShadow: progressPct >= 80 ? '0 0 10px rgba(34,197,94,0.5)' : progressPct >= 50 ? '0 0 10px rgba(245,158,11,0.5)' : '0 0 10px rgba(239,68,68,0.5)',
            }}
          />
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Active Challenges ── */}
        <GlassCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Active Challenges</h3>
            <span className="text-xs text-indigo-400 px-2 py-1 rounded-full"
              style={{ background: 'rgba(99,102,241,0.15)' }}>
              {challenges?.length || 0} active
            </span>
          </div>
          <div className="space-y-3">
            {(challenges || []).slice(0, 3).map(c => (
              <ChallengeCard key={c.id} challenge={c} />
            ))}
            {(!challenges || challenges.length === 0) && (
              <p className="text-white/30 text-sm text-center py-4">No active challenges</p>
            )}
          </div>
        </GlassCard>

        {/* ── Recent Activity ── */}
        <GlassCard>
          <h3 className="text-white font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {(recentActivity || []).slice(0, 5).map((session, i) => (
              <ActivityItem key={session.id} session={session} delay={i * 60} />
            ))}
            {(!recentActivity || recentActivity.length === 0) && (
              <p className="text-white/30 text-sm text-center py-4">No recent sessions</p>
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
